import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Stack,
  Button,
  Divider,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
// components
import {
  FormProvider,
  RHFSelect,
  RHFSwitch,
  RHFTextField,
} from '../../../components/hook-form';
import Iconify from '../../../components/Iconify';
import ImageField from 'src/sections/dashboard/diplomas/landing/components/ImageField';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { apiErrorText, mapFieldErrors } from 'src/utils/apiErrorText';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Diploma } from 'src/@types/Diploma';
import BatchMentorsPicker from './BatchMentorsPicker';
import { formatDay } from 'src/sections/dashboard/diplomas/landing/rules/format';

// ----------------------------------------------------------------------

type Props = {
  isEdit: boolean;
  currentDiploma?: Diploma;
  handleClose(saved?: boolean): void;
};

const toDateInput = (v?: string | null) => (v ? String(v).substring(0, 10) : '');

const numFromInput = (v: any, o: any) => (o === '' || o == null ? 0 : v);

const NewDiplomaSchema = Yup.object().shape({
  title: Yup.string().min(2, 'Too short').max(160).required('Title is required'),
  title_ar: Yup.string(),
  description: Yup.string(),
  description_ar: Yup.string(),
  image: Yup.string(),
  price: Yup.number().transform(numFromInput).min(0),
  durationWeeks: Yup.number().transform(numFromInput).min(0),
  recordedWeight: Yup.number().transform(numFromInput).min(0).max(100),
  active: Yup.boolean(),
  batches: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Batch name is required'),
      // Landing: "Batch 5" on the site; empty = parsed from the name.
      number: Yup.number()
        .transform((v, o) => (o === '' || o == null ? null : v))
        .nullable()
        .integer('Whole number')
        .min(1, '1 to 999')
        .max(999, '1 to 999'),
    })
  ),
});

// FEATURES B1: the API refuses more (BatchTeamService).
const MAX_CO_INSTRUCTORS = 3;

export default function DiplomaNewEditForm({
  isEdit,
  currentDiploma,
  handleClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [tracks, setTracks] = useState<any[]>([]);

  const batchInstructorId = (b: any) =>
    typeof b?.instructor === 'object' && b?.instructor
      ? b.instructor._id || ''
      : b?.instructor || '';

  const defaultValues = useMemo<any>(
    () => ({
      title: currentDiploma?.title || '',
      title_ar: (currentDiploma as any)?.title_ar || '',
      description: currentDiploma?.description || '',
      description_ar: (currentDiploma as any)?.description_ar || '',
      image: currentDiploma?.image || '',
      price: (currentDiploma as any)?.price ?? '',
      // price_usd is deliberately NOT a form value any more (the landing shows
      // no USD figure): the PATCH is a partial $set, so the stored value stays.
      durationWeeks: (currentDiploma as any)?.durationWeeks ?? '',
      recordedWeight: (currentDiploma as any)?.recordedWeight ?? 50,
      active: currentDiploma?.active ?? true,
      courses: (currentDiploma?.courses || [])
        .map((c: any) => (typeof c === 'string' ? c : c?._id))
        .filter(Boolean),
      tracks: ((currentDiploma as any)?.tracks || [])
        .map((t: any) => (typeof t === 'string' ? t : t?._id))
        .filter(Boolean),
      batches: (currentDiploma?.batches || []).map((b) => ({
        // Carry the subdoc _id through the round-trip. DiplomasRepository.updateById
        // is a blind `$set: data`, so a batch that comes back without its _id is
        // reinserted with a brand-new one — which rots Assessment.batchId and
        // makes a rename indistinguishable from a delete-plus-create for every
        // side collection keyed on (diploma, batchName).
        _id: b._id,
        name: b.name || '',
        startDate: toDateInput(b.startDate),
        endDate: toDateInput(b.endDate),
        capacity: b.capacity ?? '',
        // Landing fields: the whole batches array is replaced on save, so they
        // must round-trip or a save that edits only the title wipes them.
        number: b.number ?? '',
        enrolUntil: toDateInput(b.enrolUntil),
        instructor: batchInstructorId(b),
        // FEATURES B1: public co-instructors of this batch (Instructor ids, <= 3).
        coInstructors: ((b as any)?.coInstructors || [])
          .map((c: any) => (typeof c === 'object' && c ? c._id : c))
          .filter(Boolean),
        mentors: ((b as any)?.mentors || []).map((m: any) =>
          typeof m === 'object' && m ? m._id : m
        ),
        active: b.active ?? true,
      })),
    }),
    [currentDiploma]
  );

  const methods = useForm<any>({
    resolver: yupResolver(NewDiplomaSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods;

  const values = watch();

  const { fields, append, remove } = useFieldArray({ control, name: 'batches' });

  useEffect(() => {
    reset(defaultValues);
  }, [currentDiploma]);

  // Load instructors for the per-batch instructor dropdown (best-effort).
  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.INSTRUCTORS.LIST);
      const list = res.data?.data ?? res.data ?? [];
      setInstructors(Array.isArray(list) ? list : []);
    })().catch(() => setInstructors([]));
  }, []);

  // #4: load courses for the diploma → courses multi-select (best-effort).
  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.COURSES.LIST);
      const list = res.data?.data ?? res.data ?? [];
      setCourses(Array.isArray(list) ? list : list?.items ?? []);
    })().catch(() => setCourses([]));
  }, []);

  // Load tracks for the diploma → tracks multi-select (best-effort).
  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.TRACKS.LIST);
      const list = res.data?.data ?? res.data ?? [];
      setTracks(Array.isArray(list) ? list : list?.items ?? []);
    })().catch(() => setTracks([]));
  }, []);

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      batches: (data.batches || []).map((b: any) => {
        const batch: any = {
          name: b.name,
          startDate: b.startDate || null,
          endDate: b.endDate || null,
          capacity: b.capacity === '' || b.capacity == null ? null : Number(b.capacity),
          number: b.number === '' || b.number == null ? null : Number(b.number),
          enrolUntil: b.enrolUntil || null,
          active: b.active ?? true,
        };
        // Existing batches keep their subdoc _id; a newly added one has none and
        // must not send an empty string (BatchDto validates it as a MongoId).
        if (b._id) batch._id = b._id;
        // Only include instructor when one is chosen (DTO validates it as a MongoId).
        if (b.instructor) batch.instructor = b.instructor;
        // Co-instructors: Instructor ids, never the lead, <= 3 (the API refuses
        // anything else with a 400 under the row).
        batch.coInstructors = Array.from(new Set<string>((b.coInstructors || []).filter(Boolean)))
          .filter((id) => id !== b.instructor)
          .slice(0, MAX_CO_INSTRUCTORS);
        // Mentors: array of USER ids (not Instructor ids); empty array is fine.
        batch.mentors = (b.mentors || []).filter(Boolean);
        return batch;
      }),
    };
    if (!payload.image) delete payload.image;
    try {
      if (currentDiploma?._id) {
        await axiosInstance.patch(APIS.DIPLOMAS.PATCH(currentDiploma._id), payload);
      } else {
        await axiosInstance.post(APIS.DIPLOMAS.ADD, payload);
      }
      enqueueSnackbar(!isEdit ? 'Diploma created!' : 'Diploma updated!');
      reset();
      handleClose(true);
    } catch (err: any) {
      // Batch 400s (dates, and the team: batches.<i>.instructor / coInstructors.<j>
      // / mentors.<j>) go under their row; a list item's error goes on the list.
      const { fields: fieldErrors } = mapFieldErrors(err);
      fieldErrors
        .filter((f) => /^batches\.\d+\./.test(f.path))
        .forEach((f) => {
          const path = f.path.replace(/^(batches\.\d+\.(?:coInstructors|mentors))\.\d+$/, '$1');
          methods.setError(path, { type: 'server', message: f.message });
        });
      enqueueSnackbar(apiErrorText(err, 'Unable to save diploma'), { variant: 'error' });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h6">{isEdit ? 'Edit Diploma' : 'Create Diploma'}</Typography>
        {isEdit && currentDiploma?._id && (
          <Button
            size="small"
            endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
            onClick={() => {
              if (isDirty && !window.confirm('Discard the changes to this diploma?')) return;
              const id = currentDiploma._id as string;
              handleClose();
              navigate(PATH_DASHBOARD.diplomaLanding(id));
            }}
          >
            Landing page
          </Button>
        )}
      </Stack>

      <Stack spacing={2.5}>
        <RHFTextField name="title" label="Diploma title" />
        <RHFTextField name="title_ar" label="Arabic title" inputProps={{ dir: 'rtl' }} />
        <RHFTextField name="description" label="Description" multiline rows={3} />
        <RHFTextField
          name="description_ar"
          label="Arabic description"
          multiline
          rows={3}
          inputProps={{ dir: 'rtl' }}
        />

        <Stack direction="row" spacing={2}>
          <RHFTextField type="number" name="price" label="Price (EGP)" />
          <RHFTextField type="number" name="durationWeeks" label="Duration (weeks)" />
        </Stack>

        {/* The real thumbnail (at the cover's 1916×821 shape), Replace and ✕ —
            UploadSingleFile only ever showed a static "upload success" picture. */}
        <ImageField
          name="image"
          label="Diploma image"
          hint="The cover on the diploma page: 1916×821 px (wide banner)"
          aspect={1916 / 821}
          maxBytes={5 * 1024 * 1024}
        />

        <RHFSwitch name="active" label="Active" />

        <Divider />

        {/* #4: the courses that make up this diploma program. */}
        <Controller
          name="courses"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              options={courses}
              getOptionLabel={(o: any) => o?.name || o?.title || ''}
              isOptionEqualToValue={(o: any, v: any) => o?._id === v?._id}
              value={courses.filter((c: any) => (field.value || []).includes(c._id))}
              onChange={(_e, val: any[]) => field.onChange(val.map((v) => v._id))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Courses in this diploma"
                  placeholder="Select courses"
                />
              )}
            />
          )}
        />

        {/* Whole TRACKS in this diploma. Assigning a track links its courses and
            auto-enrols the diploma's students into them. */}
        <Controller
          name="tracks"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              options={tracks}
              getOptionLabel={(o: any) => o?.title || o?.title_ar || o?.name || ''}
              isOptionEqualToValue={(o: any, v: any) => o?._id === v?._id}
              value={tracks.filter((t: any) => (field.value || []).includes(t._id))}
              onChange={(_e, val: any[]) => field.onChange(val.map((v) => v._id))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tracks in this diploma"
                  placeholder="Select tracks"
                  helperText="Assigning a track enrolls the diploma's students in its courses."
                />
              )}
            />
          )}
        />

        {/* Dynamic combined-progress weighting: recorded (courses) vs live (batch). */}
        <RHFTextField
          type="number"
          name="recordedWeight"
          label="Recorded progress weight (%)"
          inputProps={{ min: 0, max: 100 }}
          helperText={`Recorded (courses) = ${Number(values.recordedWeight) || 0}%  ·  Live (batch attendance/quiz) = ${100 - (Number(values.recordedWeight) || 0)}%  of the student's combined diploma progress.`}
        />

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">Batches</Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() =>
              append({
                name: `Batch ${fields.length + 1}`,
                startDate: '',
                endDate: '',
                capacity: '',
                number: '',
                enrolUntil: '',
                instructor: '',
                mentors: [],
                active: true,
              })
            }
          >
            Add batch
          </Button>
        </Stack>

        {fields.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No batches yet — add Batch 1, Batch 2, …
          </Typography>
        )}

        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{ p: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}
          >
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <RHFTextField
                  name={`batches.${index}.name`}
                  label="Batch name"
                  size="small"
                  fullWidth
                />
                <IconButton color="error" onClick={() => remove(index)}>
                  <Iconify icon="eva:trash-2-outline" />
                </IconButton>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                }}
              >
                <RHFTextField
                  name={`batches.${index}.startDate`}
                  label="Start date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <RHFTextField
                  name={`batches.${index}.endDate`}
                  label="End date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <RHFTextField
                  name={`batches.${index}.capacity`}
                  label="Capacity"
                  type="number"
                  size="small"
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' },
                }}
              >
                <RHFTextField
                  name={`batches.${index}.number`}
                  label="Batch number"
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 999 }}
                  helperText="Shown on the site as 'Batch 5' / 'الدفعة الخامسة'. Earlier batches not listed here go under Landing page › Past students & their work › Past batches."
                />
                <RHFTextField
                  name={`batches.${index}.enrolUntil`}
                  label="Last day to join"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText="Empty = joining closes at the end of the start day. Set a later date to let students join a running batch (recommended: 13 days after the start; late joiners past the 2nd session get no refund, terms §22)."
                />
              </Box>
              {/* FEATURES C1: late joining is set on purpose — say so when it is. */}
              {(() => {
                const start = values?.batches?.[index]?.startDate || '';
                const until = values?.batches?.[index]?.enrolUntil || '';
                return start && until && String(until) > String(start) ? (
                  <Box>
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`Late joining until ${formatDay(String(until))}`}
                    />
                  </Box>
                ) : null;
              })()}
              <RHFSelect
                name={`batches.${index}.instructor`}
                label="Lead instructor"
                size="small"
              >
                <option value="">— No instructor —</option>
                {instructors.map((ins) => (
                  <option key={ins._id} value={ins._id}>
                    {ins.email ? `${ins.name} — ${ins.email}` : ins.name}
                  </option>
                ))}
              </RHFSelect>
              {/* FEATURES B1/B5: co-instructors are PUBLIC teachers (Instructor
                  profiles) who co-run this batch; <= 3, never the lead. */}
              <Controller
                name={`batches.${index}.coInstructors`}
                control={control}
                render={({ field, fieldState }) => {
                  const lead = values?.batches?.[index]?.instructor || '';
                  const ids: string[] = (field.value || []).filter((id: string) => id !== lead);
                  const byId = new Map(instructors.map((ins) => [String(ins._id), ins]));
                  const options = instructors
                    .filter((ins) => ins.active !== false || ids.includes(String(ins._id)))
                    .filter((ins) => String(ins._id) !== lead)
                    .map((ins) => String(ins._id));
                  return (
                    <Autocomplete
                      multiple
                      size="small"
                      options={options}
                      value={ids}
                      getOptionLabel={(id: string) => {
                        const ins: any = byId.get(String(id));
                        if (!ins) return String(id);
                        return `${ins.name}${ins.active === false ? ' (inactive)' : ''}`;
                      }}
                      getOptionDisabled={(id: string) =>
                        ids.length >= MAX_CO_INSTRUCTORS && !ids.includes(id)
                      }
                      onChange={(_e, vals: string[]) => field.onChange(vals.slice(0, MAX_CO_INSTRUCTORS))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Co-instructors"
                          placeholder={ids.length ? '' : 'Add up to 3 instructors'}
                          error={!!fieldState.error}
                          helperText={
                            fieldState.error?.message ||
                            'Optional. Shown with the lead instructor on this batch\'s card. The lead runs every class; a co-instructor runs the classes assigned to them and classes with no teacher.'
                          }
                        />
                      )}
                    />
                  );
                }}
              />
              {/* Mentors co-teach this batch: plain USER accounts (not public
                  instructors) that can run the classes with no teacher set. */}
              <Controller
                name={`batches.${index}.mentors`}
                control={control}
                render={({ field, fieldState }) => (
                  <BatchMentorsPicker
                    value={field.value || []}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    initialUsers={
                      ((currentDiploma?.batches?.[index] as any)?.mentors || []).filter(
                        (m: any) => m && typeof m === 'object'
                      )
                    }
                  />
                )}
              />
              <RHFSwitch name={`batches.${index}.active`} label="Batch active" />
            </Stack>
          </Box>
        ))}
      </Stack>

      <Stack alignItems="flex-end" sx={{ mt: 3 }}>
        <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
          {!isEdit ? 'Create Diploma' : 'Save Changes'}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
